import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Loop } from '@/common/api/types/loops';
import type { ObjectiveHealth } from '@/common/api/types/objectives';
import { FINDING_GROUP_ORDER } from '@/features/agentic-insights/components/data/groupFindings';
import type { PersonaView } from '@/features/agentic-insights/components/data/resolvePersonaView';
import type { FindingGroup } from '@/features/agentic-insights/components/data/types';
import type { FindingEntry } from '@/features/agentic-insights/components/experimental/shared/data/findingEntries';
import { GroupHeading } from '@/features/agentic-insights/components/experimental/shared/findings/GroupHeading';
import { entryKey } from '../data/aggregateFindings';
import { GROUP_TONE, MEASURE, TYPE } from '../language';
import { FindingArgument } from './FindingArgument';
import { FindingRow } from './FindingRow';

const EMPTY_COLLAPSED: ReadonlySet<FindingGroup> = new Set();

interface FindingListProps {
  /** Carries a `loop` per entry only in the All loops reading — see `LoopStamp`. */
  entries: (FindingEntry & { loop?: Loop })[];
  views: Map<string, PersonaView>;
  /** Keyed by objective id, for the meta line's objective-scope entry. Undefined while loading. */
  objectiveHealthById: Record<number, ObjectiveHealth> | undefined;
  isObjectiveHealthPending: boolean;
  openFindingId: string | undefined;
  onSwitchLoop?: (loopId: number) => void;
  onOpenFinding: (findingId: string | undefined) => void;
}

/**
 * A section per finding group, in the contract's own severity order, each numbered from 1 so it
 * reads as its own short briefing.
 *
 * Selecting a entry opens it in place rather than navigating anywhere: this view keeps the list
 * and the detail together, and being thrown into a different mode to read one finding loses the
 * place you were reading from. The Details mode is the same two things weighted the other way — a
 * rail and one finding open — so a reader picks the balance they want and stays there.
 *
 * A group with no findings renders nothing at all: "0 on track" reads as a claim that nothing is
 * healthy, when it almost always means nothing was reported either way.
 */
export const FindingList = ({
  entries,
  views,
  objectiveHealthById,
  isObjectiveHealthPending,
  openFindingId,
  onSwitchLoop,
  onOpenFinding,
}: FindingListProps) => {
  const { t } = useTranslation();

  /**
   * Which groups are put away, in component state only. Deliberately not in the URL and not stored:
   * it is housekeeping while you read one section, not a view worth reproducing for someone else, and
   * a link that arrived with half the run hidden would look like a run that found less than it did.
   */
  const [collapsed, setCollapsed] = useState<ReadonlySet<FindingGroup>>(EMPTY_COLLAPSED);

  const toggle = (group: FindingGroup) =>
    setCollapsed((current) => {
      const next = new Set(current);
      if (!next.delete(group)) next.add(group);
      return next;
    });

  if (entries.length === 0) {
    return (
      <p data-testid="agentic-x-findings-empty" className={`${TYPE.body} ${MEASURE} py-12`}>
        {t(($) => $.loopAgenticInsights.experimental.findings.empty)}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-10">
      {FINDING_GROUP_ORDER.map((group) => {
        const inGroup = entries.filter((entry) => entry.group === group);
        if (inGroup.length === 0) return null;

        const isCollapsed = collapsed.has(group);

        return (
          <div key={group} className="flex flex-col">
            <div className="pb-1">
              <GroupHeading
                type={TYPE}
                groupTone={GROUP_TONE}
                group={group}
                count={inGroup.length}
                collapsed={isCollapsed}
                onToggle={() => toggle(group)}
              />
            </div>
            {isCollapsed
              ? null
              : inGroup.map((entry, indexInGroup) => {
                  const key = entryKey(entry);
                  const view = views.get(key);
                  if (view?.kind !== 'voiced') return null;
                  const isOpen = key === openFindingId;

                  return (
                    <div key={key} data-expanded={isOpen}>
                      <FindingRow
                        finding={entry.finding}
                        view={view}
                        ordinal={entry.ordinal}
                        loop={entry.loop}
                        onSwitchLoop={onSwitchLoop}
                        objectiveHealthById={objectiveHealthById}
                        isObjectiveHealthPending={isObjectiveHealthPending}
                        isOpen={isOpen}
                        isFirstInGroup={indexInGroup === 0}
                        onOpen={() => onOpenFinding(isOpen ? undefined : key)}
                      />
                      {isOpen ? (
                        <div
                          data-testid="agentic-x-inline-detail"
                          className="flex flex-col gap-10 border-l-2 border-border-brand py-6 pr-4 pl-9"
                        >
                          <FindingArgument finding={entry.finding} view={view} />
                        </div>
                      ) : null}
                    </div>
                  );
                })}
          </div>
        );
      })}
    </div>
  );
};
